import React from 'react';
import { Card, CardContent } from './ui/card';
import { CheckCircle } from 'lucide-react';

export const IntegrationsSection = () => {
  const integrations = [
    {
      name: "Epic",
      logo: "EP",
      description: "Full EHR and patient management integration",
      features: ["Patient sync", "Appointment scheduling", "Clinical notes"]
    },
    {
      name: "Cerner",
      logo: "CR",
      description: "Seamless EMR and practice management connection",
      features: ["EMR integration", "Billing sync", "Patient records"]
    },
    {
      name: "Athenahealth", 
      logo: "AH",
      description: "Complete practice management system integration",
      features: ["Online booking", "Payment processing", "Patient communications"]
    },
    {
      name: "NextGen",
      logo: "NG",
      description: "Advanced healthcare software integration",
      features: ["Care planning", "Progress tracking", "Insurance management"]
    }
  ];

  return (
    <section id="integrations" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-6 mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
            Deep Integrations into Your
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-green-700">
              {" "}Existing PMS
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Seamlessly connect with your current healthcare practice management system. 
            No disruption to your workflow, just enhanced automation.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {integrations.map((integration, index) => (
            <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white hover:bg-gradient-to-br hover:from-white hover:to-gray-50">
              <CardContent className="p-6 text-center space-y-4">
                <div className="mx-auto w-16 h-16 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="text-white font-bold text-lg">{integration.logo}</span>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-gray-900">{integration.name}</h3>
                  <p className="text-sm text-gray-600">{integration.description}</p>
                </div>
                
                <div className="space-y-2 pt-2">
                  {integration.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center space-y-6">
          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 max-w-4xl mx-auto">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Why Our Integrations Work Better</h3>
            <div className="grid md:grid-cols-3 gap-6 text-sm text-gray-600">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">Real-time Sync</p>
                  <p>Instant updates between Agents.audio and your PMS</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">Secure Connection</p>
                  <p>HIPAA-compliant data transfer and storage</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">Easy Setup</p>
                  <p>Our team handles the entire integration process</p>
                </div>
              </div>
            </div>
          </div>
          
          <p className="text-gray-600">
            Don't see your PMS listed? 
            <a href="#contact" className="text-gray-900 hover:text-gray-700 font-medium ml-1">
              Contact us for custom integrations
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};